/**
 * Schedule Utilities
 *
 * This file contains standardized utilities for working with tutor schedules
 * and availability data. These functions ensure consistent time slot
 * formatting across the application and proper data structures for API calls.
 */
import { LanguageVariant, detectLanguageVariant } from './locale-utils';
/**
 * Array of lowercase day names in correct order
 */
export const DAYS_OF_WEEK = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
/**
 * Get day of week string from Date object
 * @param date Date to get day from
 * @returns Lowercase day name (e.g., "monday")
 */
export function getDayOfWeek(date) {
    return DAYS_OF_WEEK[date.getDay()];
}
/**
 * Local calendar date as YYYY-MM-DD (same as student-schedule / `toLocaleDateString('en-CA')`).
 * Use instead of `date.toISOString().split('T')[0]`, which uses UTC and can shift the calendar day.
 */
export function toLocalDateKey(date) {
    return date.toLocaleDateString('en-CA');
}
/**
 * Build UTC ISO instants for a lesson from a local calendar date and wall-clock slot.
 * Use for all booking flows so times align with the student's (or viewer's) local date — never append
 * `Z` to local HH:mm (that stored the wall time as UTC and shifted lessons).
 *
 * @param calendarDateKey - YYYY-MM-DD in the user's local calendar
 * @param timeSlot - "HH:mm-HH:mm" or a single "HH:mm" (end computed from durationHours)
 * @param durationHours - used only when timeSlot has no range (default 1)
 */
export function buildLocalLessonIsoRange(calendarDateKey, timeSlot, durationHours = 1) {
    const trimmed = timeSlot.trim();
    if (!trimmed) {
        throw new Error('timeSlot is required');
    }
    // "09:00-10:00" has a single hyphen between wall-clock start and end (times use colons, not hyphens)
    const dashIdx = trimmed.indexOf('-');
    let startPart;
    let endPart;
    if (dashIdx === -1) {
        startPart = trimmed;
        endPart = null;
    }
    else {
        startPart = trimmed.slice(0, dashIdx).trim();
        endPart = trimmed.slice(dashIdx + 1).trim();
        if (!endPart) {
            endPart = null;
        }
    }
    const [y, m, d] = calendarDateKey.split('-').map((n) => parseInt(n, 10));
    if (!y || !m || !d || Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) {
        throw new Error('Invalid calendar date; expected YYYY-MM-DD');
    }
    const parseHm = (s) => {
        const seg = s.trim().split(':');
        const h = parseInt(seg[0] ?? '0', 10);
        const min = parseInt(seg[1] ?? '0', 10);
        return { h: Number.isNaN(h) ? 0 : h, min: Number.isNaN(min) ? 0 : min };
    };
    const base = new Date(y, m - 1, d);
    const { h: sh, min: smin } = parseHm(startPart);
    const startDateTime = new Date(base);
    startDateTime.setHours(sh, smin, 0, 0);
    let endDateTime;
    if (endPart) {
        const { h: eh, min: emin } = parseHm(endPart);
        endDateTime = new Date(base);
        endDateTime.setHours(eh, emin, 0, 0);
    }
    else {
        endDateTime = new Date(startDateTime.getTime() + durationHours * 60 * 60 * 1000);
    }
    if (startDateTime.getTime() >= endDateTime.getTime()) {
        throw new Error('Start time must be before end time');
    }
    return {
        start_time: startDateTime.toISOString(),
        end_time: endDateTime.toISOString(),
    };
}
/**
 * Normalize `date_overrides[].date` from API (plain YYYY-MM-DD or ISO timestamp) to YYYY-MM-DD for map keys and comparisons.
 */
export function normalizeOverrideDateKey(dateStr) {
    if (!dateStr)
        return '';
    return dateStr.split('T')[0];
}
/**
 * Format a time value with leading zeros
 * @param hour Hour value (0-23)
 * @param minute Minute value (0-59)
 * @returns Formatted time string (e.g., "09:00")
 */
export function formatTime(hour, minute = 0) {
    return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
}
/**
 * Parse a UI slot string ("9:00", "11:30", "09:30") to hour and minute.
 */
export function parseUiSlotHourMinute(slot) {
    const parts = slot.trim().split(':');
    const hour = parseInt(parts[0] ?? '0', 10);
    const minute = parseInt(parts[1] ?? '0', 10);
    return {
        hour: Number.isNaN(hour) ? 0 : hour,
        minute: Number.isNaN(minute) ? 0 : minute,
    };
}
/**
 * 12-hour label for tutor schedule rows (e.g. "11:30 AM").
 */
export function formatWallClock12hLabel(hour24, minute) {
    const ampm = hour24 >= 12 ? 'PM' : 'AM';
    const hour12 = hour24 > 12 ? hour24 - 12 : hour24 === 0 ? 12 : hour24;
    return `${hour12}:${minute.toString().padStart(2, '0')} ${ampm}`;
}
/** Parse labels from {@link formatWallClock12hLabel} back to minutes since midnight for sorting. */
export function parseTwelveHourLabelToMinutes(label) {
    const parts = label.trim().split(/\s+/);
    if (parts.length < 2)
        return 0;
    const timePart = parts[0] ?? '0:00';
    const ampm = (parts[1] ?? 'AM').toUpperCase();
    const [hs, ms = '0'] = timePart.split(':');
    let hours = parseInt(hs, 10);
    const minutes = parseInt(ms, 10);
    if (Number.isNaN(hours))
        hours = 0;
    const safeMin = Number.isNaN(minutes) ? 0 : minutes;
    if (ampm === 'PM' && hours < 12)
        hours += 12;
    if (ampm === 'AM' && hours === 12)
        hours = 0;
    return hours * 60 + safeMin;
}
/**
 * Convert a simple time string to a TimeSlot object with start_time and end_time
 * @param slot Simple time string (e.g., "9:00", "11:30")
 * @param durationInMinutes Duration in minutes
 * @returns TimeSlot object with start_time and end_time
 */
export function formatTimeSlotForApi(slot, durationInMinutes) {
    const { hour, minute } = parseUiSlotHourMinute(slot);
    const startTime = formatTime(hour, minute);
    const startTotal = hour * 60 + minute;
    const endTotal = Math.min(startTotal + durationInMinutes, 23 * 60 + 59);
    const endH = Math.floor(endTotal / 60);
    const endM = endTotal % 60;
    const endTime = formatTime(endH, endM);
    return { start_time: startTime, end_time: endTime };
}
/**
 * Extract simple time from API format
 * @param slot TimeSlot object with start_time and end_time
 * @returns Simple time string (e.g., "9:00", "11:30") — hour without unnecessary leading zero; minutes zero-padded
 */
export function parseTimeSlotFromApi(slot) {
    try {
        // Handle null or undefined
        if (!slot) {
            console.warn('parseTimeSlotFromApi received null or undefined slot');
            return '';
        }
        // Handle string inputs (already formatted)
        if (typeof slot === 'string') {
            const trimmed = slot.trim();
            if (/^\d{1,2}:\d{2}$/.test(trimmed)) {
                const [hs, ms] = trimmed.split(':');
                const hourNum = parseInt(hs ?? '0', 10);
                const minStr = (ms ?? '00').padStart(2, '0');
                return `${hourNum}:${minStr}`;
            }
            return trimmed;
        }
        // Handle object but not TimeSlot (missing properties)
        if (typeof slot === 'object' && (!slot.start_time || typeof slot.start_time !== 'string')) {
            console.warn('parseTimeSlotFromApi received invalid object:', slot);
            return '';
        }
        // Standard case: proper TimeSlot object
        const time = slot.start_time;
        // HH:MM (API / Postgres style)
        if (/^\d{2}:\d{2}$/.test(time)) {
            const hourNum = parseInt(time.slice(0, 2), 10);
            const minPart = time.slice(3, 5);
            return `${hourNum}:${minPart}`;
        }
        // Single-digit hour or other wall-clock forms
        const hm = /^(\d{1,2}):(\d{2})$/.exec(time);
        if (hm) {
            const hourNum = parseInt(hm[1], 10);
            return `${hourNum}:${hm[2]}`;
        }
        console.warn(`Invalid time format in parseTimeSlotFromApi: ${time}`);
        return time;
    }
    catch (error) {
        console.error('Error in parseTimeSlotFromApi:', error, 'for slot:', slot);
        return '';
    }
}
/**
 * Format a time slot for display based on user's locale
 * @param slot TimeSlot object or simple time string
 * @returns Formatted time string for display
 */
export function formatTimeSlotForDisplay(slot) {
    const language = detectLanguageVariant();
    const isUK = language === LanguageVariant.UK_ENGLISH;
    try {
        let hours, minutes;
        if (typeof slot === 'string') {
            [hours, minutes] = slot.split(':').map(Number);
        }
        else {
            [hours, minutes] = slot.start_time.split(':').map(Number);
        }
        if (isNaN(hours) || isNaN(minutes)) {
            return typeof slot === 'string' ? slot : slot.start_time;
        }
        // Create a Date object to use toLocaleTimeString
        const date = new Date();
        date.setHours(hours, minutes, 0);
        return date.toLocaleTimeString(language, {
            hour: 'numeric',
            minute: '2-digit',
            hour12: !isUK // 12-hour format for US, 24-hour for UK
        });
    }
    catch (error) {
        console.error('Error formatting time slot:', error);
        return typeof slot === 'string' ? slot : slot.start_time;
    }
}
/**
 * Create a properly formatted availability payload for API
 * @param tutorId Tutor ID
 * @param dayOfWeek Day of week (lowercase)
 * @param timeSlots Array of time strings
 * @param durationMinutes Duration in minutes
 * @param dateString ISO date string (YYYY-MM-DD)
 * @returns Properly formatted availability payload
 */
export function createAvailabilityPayload(tutorId, dayOfWeek, timeSlots, durationMinutes, dateString) {
    // Convert number strings to actual numbers
    const duration = typeof durationMinutes === 'string'
        ? parseInt(durationMinutes, 10)
        : durationMinutes;
    // Create the basic time slots first - these should just have start_time and end_time
    const basicTimeSlots = timeSlots.map(slot => formatTimeSlotForApi(slot, duration));
    // For date-specific availability, we only want to create a date override
    // and NOT populate the weekly schedule for that day of the week
    // This prevents the schedule from appearing on all dates of that day
    return {
        tutor_id: tutorId,
        weekly_schedule: {}, // Empty weekly schedule - this preserves existing weekly schedule
        date_overrides: [{
                date: dateString,
                slots: basicTimeSlots
            }],
        lesson_duration: duration
    };
}
/**
 * Validate a time slot object
 * @param slot Object to validate
 * @returns Whether the object is a valid TimeSlot
 */
export function validateTimeSlot(slot) {
    return (typeof slot === 'object' &&
        typeof slot.start_time === 'string' &&
        typeof slot.end_time === 'string' &&
        /^\d{2}:\d{2}$/.test(slot.start_time) &&
        /^\d{2}:\d{2}$/.test(slot.end_time));
}
/**
 * Validate an availability payload
 * @param payload Object to validate
 * @returns Whether the object is a valid AvailabilityPayload
 */
export function validateAvailabilityPayload(payload) {
    // Check basic structure
    if (!payload ||
        typeof payload !== 'object' ||
        typeof payload.lesson_duration !== 'number') {
        console.error('Invalid payload structure or lesson duration');
        return false;
    }
    // Check weekly_schedule
    if (!payload.weekly_schedule ||
        typeof payload.weekly_schedule !== 'object') {
        console.error('Invalid weekly_schedule structure');
        return false;
    }
    // Check each day in weekly_schedule
    for (const day in payload.weekly_schedule) {
        if (!DAYS_OF_WEEK.includes(day)) {
            console.error(`Invalid day name: ${day}`);
            return false;
        }
        const slots = payload.weekly_schedule[day];
        if (!Array.isArray(slots)) {
            console.error(`Slots for ${day} is not an array`);
            return false;
        }
        for (const slot of slots) {
            if (!validateTimeSlot(slot)) {
                console.error(`Invalid time slot in ${day}:`, slot);
                return false;
            }
        }
    }
    // Check date_overrides
    if (!Array.isArray(payload.date_overrides)) {
        console.error('date_overrides is not an array');
        return false;
    }
    for (const override of payload.date_overrides) {
        if (!override.date ||
            typeof override.date !== 'string' ||
            !/^\d{4}-\d{2}-\d{2}$/.test(override.date)) {
            console.error('Invalid date format in date_overrides:', override.date);
            return false;
        }
        if (!Array.isArray(override.slots)) {
            console.error('Slots in date_override is not an array');
            return false;
        }
        for (const slot of override.slots) {
            if (!validateTimeSlot(slot)) {
                console.error(`Invalid time slot in date_override ${override.date}:`, slot);
                return false;
            }
        }
    }
    return true;
}
function normalizeWeeklyScheduleForDayKeys(weekly) {
    if (!weekly || typeof weekly !== 'object')
        return {};
    const out = {};
    for (const [key, slots] of Object.entries(weekly)) {
        if (!Array.isArray(slots))
            continue;
        const day = key.toLowerCase();
        const normalized = [];
        for (const s of slots) {
            if (s && typeof s === 'object' && 'start_time' in s && 'end_time' in s) {
                const ts = s;
                if (validateTimeSlot(ts))
                    normalized.push(ts);
            }
        }
        out[day] = [...(out[day] || []), ...normalized];
    }
    return out;
}
/**
 * Resolve availability for a calendar day: date override for that YYYY-MM-DD wins, else weekly schedule for the weekday.
 */
export function getAvailabilitySlotsForLocalDate(date, availability) {
    const defaultDuration = availability && typeof availability.lesson_duration === 'number' ? availability.lesson_duration : 30;
    if (!availability) {
        return { slots: [], lessonDurationMinutes: defaultDuration };
    }
    const dateKey = toLocalDateKey(date);
    const overrides = availability.date_overrides || [];
    for (const o of overrides) {
        if (normalizeOverrideDateKey(o.date) === dateKey) {
            const raw = Array.isArray(o.slots) ? o.slots : [];
            const slots = raw.filter((s) => validateTimeSlot(s));
            return { slots, lessonDurationMinutes: defaultDuration };
        }
    }
    const weekly = normalizeWeeklyScheduleForDayKeys(availability.weekly_schedule);
    const day = getDayOfWeek(date);
    return { slots: weekly[day] || [], lessonDurationMinutes: defaultDuration };
}
/** Minutes since midnight from HH:MM (24h). */
export function parseTimeToMinutesHHMM(hhmm) {
    if (!hhmm || typeof hhmm !== 'string')
        return 0;
    const [h, m] = hhmm.split(':').map((x) => parseInt(x, 10));
    if (Number.isNaN(h) || Number.isNaN(m))
        return 0;
    return h * 60 + m;
}
export function localMinutesSinceMidnight(d) {
    return d.getHours() * 60 + d.getMinutes();
}
/** True if `lessonStart` falls on `calendarDate` and lies in [slot.start, slot.end). */
export function timeRangeContainsLocalLesson(slot, lessonStart, calendarDate) {
    if (toLocalDateKey(lessonStart) !== toLocalDateKey(calendarDate))
        return false;
    const ls = parseTimeToMinutesHHMM(slot.start_time);
    const le = parseTimeToMinutesHHMM(slot.end_time);
    const lm = localMinutesSinceMidnight(lessonStart);
    if (le > ls)
        return lm >= ls && lm < le;
    return lm >= ls;
}
